import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { collection, query, doc as fDoc, limit } from 'firebase/firestore';
import { secureGetDocs as getDocs, secureGetDoc as getDoc } from '../../lib/secureApplicationsRepo';
import { resolveMergeFields } from '../../lib/variables/resolver';
import { workspaceHeader } from '../../lib/workspaces';
import { notifyToast } from '../../lib/toastStore';
import { buildApplicantPdfFileName } from '../../lib/pdfFileName';
import { db } from '../../lib/firebase';
import { getAuth } from 'firebase/auth';
import Handlebars from 'handlebars';
import {
  X, Save, Eye, Code, FileText, AlertCircle, CheckCircle, Loader,
  ChevronDown, Hash, Printer, Download, Search, User, Settings, AlignLeft,
  RotateCcw, Sparkles, Sliders, Layout, Monitor, Smartphone, Plus, Check, ChevronRight
} from 'lucide-react';
import Modal from '../shared/Modal';
import SmartHtmlEditor, { type SmartHtmlEditorHandle } from './SmartHtmlEditor';
import { TiptapEditor } from './TiptapEditor';
import { DocumentTemplate, PaperSize, Orientation } from '../../types';
import {
  TemplateVariable,
  getTemplateVariables,
  getGroupedTemplateVariables,
  get
} from '../../lib/variables/service';
import { migrateVariableGroupsToFirestore } from '../../lib/variables/migration';

interface TemplateEditorProps {
  template: DocumentTemplate;
  onSave: (template: DocumentTemplate) => void;
  onClose: () => void;
  saving: boolean;
  adminEmail: string;
}

/* ── Simple HTML indent formatter ── */
function formatHtml(html: string): string {
  let indent = 0;
  const tab = '  ';
  const lines = html.split(/\n/).map((l) => l.trim()).filter((l) => l !== '');
  const result: string[] = [];
  for (let line of lines) {
    if (/^\s*\u003c\/\w/.test(line)) indent = Math.max(0, indent - 1);
    result.push(tab.repeat(indent) + line);
    if (/^\s*\u003c\w[^\u003e]*[^\/\u003e]\u003e\s*$/.test(line)) indent++;
  }
  return result.join('\n');
}

export default function TemplateEditor({ template, onSave, onClose, saving, adminEmail }: TemplateEditorProps) {
  const [name, setName] = useState(template.name);
  const [description, setDescription] = useState(template.description);
  const [category, setCategory] = useState(template.category);
  const [paperSize, setPaperSize] = useState<PaperSize>(template.paperSize);
  const [orientation, setOrientation] = useState<Orientation>(template.orientation);
  const [margins, setMargins] = useState(template.margins);
  const [htmlContent, setHtmlContent] = useState(template.htmlContent);
  const [css, setCss] = useState(template.css);
  const [editorMode, setEditorMode] = useState<'visual' | 'html' | 'split'>('visual');
  const [showVars, setShowVars] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [previewZoom, setPreviewZoom] = useState<number>(100);
  const [errors, setErrors] = useState<string[]>([]);
  const [htmlValidationErrors, setHtmlValidationErrors] = useState<string[]>([]);
  const [editorTab, setEditorTab] = useState<'html' | 'css'>('html');
  const [visualHtml, setVisualHtml] = useState<string | null>(null);

  // Dynamic Variable Registry
  const [registryVariables, setRegistryVariables] = useState<TemplateVariable[]>([]);
  const [variableGroups, setVariableGroups] = useState<Array<{ label: string; variables: TemplateVariable[] }>>([]);
  const [varSearch, setVarSearch] = useState('');

  // Applicant preview data
  const [applicantList, setApplicantList] = useState<{ id: string; name: string; email: string; appId: string }[]>([]);
  const [applicantSearch, setApplicantSearch] = useState('');
  const [applicantDropdownOpen, setApplicantDropdownOpen] = useState(false);
  const [selectedApplicantId, setSelectedApplicantId] = useState('');
  const [applicantContext, setApplicantContext] = useState<Record<string, any> | null>(null);
  const [loadingApplicant, setLoadingApplicant] = useState(false);
  const applicantDropdownRef = useRef<HTMLDivElement>(null);
  const [generating, setGenerating] = useState(false);
  const [generateResult, setGenerateResult] = useState<{ pdfUrl: string; documentId: string } | null>(null);
  const htmlEditorRef = useRef<SmartHtmlEditorHandle>(null);
  const previewFrameRef = useRef<HTMLIFrameElement>(null);

  // Fetch / Seed Variable Registry
  const refreshVariables = useCallback(async () => {
    try {
      await migrateVariableGroupsToFirestore();
      const allVars = await getTemplateVariables(true);
      setRegistryVariables(allVars);
      const grouped = await getGroupedTemplateVariables(false);
      setVariableGroups(grouped);
    } catch (e) {
      console.error('Failed to load variable registry:', e);
    }
  }, []);

  useEffect(() => {
    refreshVariables();
  }, [refreshVariables]);

  const allVariables = useMemo(() => {
    return registryVariables
      .filter((v) => !v.deprecated)
      .map((v) => ({
        name: v.name,
        label: v.label,
        description: `Insert {{${v.name}}}`,
        group: v.group,
      }));
  }, [registryVariables]);

  // Build Sample Data dynamically from Registry
  const buildSampleDataFromRegistry = useCallback((): Record<string, any> => {
    const now = new Date();
    const result: Record<string, any> = {};

    registryVariables.forEach((v) => {
      if (!v.deprecated && v.sampleValue !== undefined) {
        if (v.name.includes('.')) {
          const parts = v.name.split('.');
          let curr = result;
          for (let i = 0; i < parts.length - 1; i++) {
            const p = parts[i];
            if (!curr[p] || typeof curr[p] !== 'object') {
              curr[p] = {};
            }
            curr = curr[p];
          }
          curr[parts[parts.length - 1]] = v.sampleValue;
        } else {
          result[v.name] = v.sampleValue;
        }
      }
    });

    result.education = [
      { institution: 'State University', schoolName: 'State University', degree: 'Bachelor of Science', field: 'Hospitality Management', startDate: 'August 2018', endDate: 'May 2022', gpa: '3.5', honors: "Dean's List", city: 'Boston', country: 'United States', address: '100 University Ave, Boston, MA', courses: 'Hotel Operations, Event Management, Accounting' },
      { institution: 'Community College', schoolName: 'Community College', degree: 'Associate Degree', field: 'General Studies', startDate: 'September 2016', endDate: 'June 2018', gpa: '3.8', honors: 'Honors Program', city: 'Chicago', country: 'United States', address: '200 College Dr, Chicago, IL', courses: 'English, Math, Science' },
    ];
    result.employment = [
      { companyName: 'Marriott Hotels', employer: 'Marriott Hotels', position: 'Front Desk Associate', description: 'Managed front desk operations.', mainResponsibilities: 'Guest check-in/out, reservations', additionalResponsibilities: 'Training new staff', startDate: 'June 2022', endDate: 'Present', city: 'New York', country: 'United States', salary: '42,000', supervisorName: 'Jane Smith' },
      { companyName: 'Hilton Hotels', employer: 'Hilton Hotels', position: 'Concierge', description: 'Assisted guests with recommendations.', mainResponsibilities: 'Guest assistance, travel booking', startDate: 'January 2020', endDate: 'May 2022', city: 'Chicago', country: 'United States', salary: '38,000', supervisorName: 'Bob Johnson' },
    ];
    result.emergencyContacts = [{ name: 'Jane Doe', phone: '+1 (555) 987-6543', email: 'jane@example.com', relationship: 'Spouse' }];
    result.today = now.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    result.currentDate = now.toLocaleString();

    return result;
  }, [registryVariables]);

  /* ── Fetch applicants ── */
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(query(collection(db, 'applications'), limit(500)));
        const list: { id: string; name: string; email: string; appId: string }[] = [];
        snap.forEach((d) => {
          const data = d.data();
          const pd = data.personalDetails || {};
          list.push({
            id: d.id,
            name: `${pd.firstName || ''} ${pd.lastName || ''}`.trim() || data.applicationId || d.id,
            email: data.userEmail || pd.email || '',
            appId: data.applicationId || d.id,
          });
        });
        list.sort((a, b) => a.name.localeCompare(b.name));
        setApplicantList(list);
      } catch (e) {
        console.error('Failed to fetch applicants:', e);
        setApplicantList([]);
      }
    })();
  }, []);

  const filteredApplicants = useMemo(() => {
    if (!applicantSearch.trim()) return applicantList;
    const q = applicantSearch.toLowerCase();
    return applicantList.filter(
      (a) =>
        a.name.toLowerCase().includes(q) ||
        a.email.toLowerCase().includes(q) ||
        a.appId.toLowerCase().includes(q)
    );
  }, [applicantSearch, applicantList]);

  /* ── Click-outside for applicant dropdown ── */
  useEffect(() => {
    if (!applicantDropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (applicantDropdownRef.current && !applicantDropdownRef.current.contains(e.target as Node)) {
        setApplicantDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [applicantDropdownOpen]);

  /* ── Build applicant context ── */
  useEffect(() => {
    if (!selectedApplicantId) {
      setApplicantContext(null);
      return;
    }
    setLoadingApplicant(true);
    (async () => {
      try {
        let appRef = fDoc(db, 'applications', selectedApplicantId);
        let appSnap = await getDoc(appRef);
        if (!appSnap.exists()) {
          // Fallback: search by applicationId
          const q = query(collection(db, 'applications'), limit(500));
          const allDocs = await getDocs(q);
          const found = allDocs.docs.find(d => d.id === selectedApplicantId || d.data().applicationId === selectedApplicantId);
          if (found) {
            appSnap = found as any;
          }
        }
        if (!appSnap || !appSnap.exists()) {
          setApplicantContext(null);
          setLoadingApplicant(false);
          return;
        }
        const application = appSnap.data();
        const ctx: Record<string, any> = { _rawApplication: application };

        registryVariables.forEach((v) => {
          if (v.dataPath) {
            const val = get(application, v.dataPath);
            if (val !== undefined && val !== null) {
              if (v.name.includes('.')) {
                const parts = v.name.split('.');
                let curr = ctx;
                for (let i = 0; i < parts.length - 1; i++) {
                  const p = parts[i];
                  if (!curr[p] || typeof curr[p] !== 'object') {
                    curr[p] = {};
                  }
                  curr = curr[p];
                }
                curr[parts[parts.length - 1]] = val;
              } else {
                ctx[v.name] = val;
              }
            }
          }
        });

        const pd = application?.personalDetails || {};
        const assign = application?.assignment || {};
        const now = new Date();

        ctx.fullName = [pd.firstName, pd.lastName].filter(Boolean).join(' ') || ctx.fullName || '';
        ctx.email = application?.userEmail || pd.email || ctx.email || '';
        ctx.hostAddress = [assign.city, assign.state, assign.country].filter(Boolean).join(', ') || '';
        ctx.programName = assign.employerName || application?.programType || application?.programId || '';
        ctx.today = now.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
        ctx.currentDate = now.toLocaleString();

        ctx.education = (application?.educationalBackground || []).map((e: any) => ({
          institution: e.institutionName || e.institution || '',
          schoolName: e.institutionName || e.institution || '',
          degree: e.degreeEarned || e.degree || '',
          field: e.fieldOfStudy || '',
          startDate: e.startDate || '',
          endDate: e.endDate || '',
          gpa: e.gpa || '',
          honors: e.honors || '',
          address: e.institutionAddress || '',
          city: e.city || '',
          country: e.country || '',
          courses: e.courses || '',
        }));

        ctx.employment = (application?.workExperience || []).map((w: any) => ({
          employer: w.employerName || w.employer || '',
          companyName: w.employerName || w.employer || '',
          position: w.jobTitle || w.position || '',
          description: w.jobDescription || '',
          mainResponsibilities: w.mainResponsibilities || w.jobDescription || '',
          additionalResponsibilities: w.additionalResponsibilities || '',
          startDate: w.startDate || '',
          endDate: w.endDate || '',
          salary: w.salary || '',
          supervisorName: w.supervisorName || '',
          supervisorContact: w.supervisorContact || '',
          address: w.employerAddress || '',
          city: w.city || '',
          country: w.country || '',
        }));

        const emergencyContacts = (application?.emergencyContacts || []).map((ec: any) => ({
          name: ec.fullName || ec.name || '',
          phone: ec.phone || '',
          email: ec.email || '',
          relationship: ec.relationship || '',
        }));
        ctx.emergencyContacts = emergencyContacts;
        ctx.emergencyName = emergencyContacts.length > 0 ? emergencyContacts[0].name : '';
        ctx.emergencyPhone = emergencyContacts.length > 0 ? emergencyContacts[0].phone : '';
        ctx.emergencyRelationship = emergencyContacts.length > 0 ? emergencyContacts[0].relationship : '';

        setApplicantContext(ctx);
      } catch (e) {
        console.error('Failed to load applicant data:', e);
        setApplicantContext(null);
      } finally {
        setLoadingApplicant(false);
      }
    })();
  }, [selectedApplicantId, registryVariables]);

  /* ── Renderable HTML ── */
  const renderedHtml = useMemo(() => {
    const data = applicantContext || buildSampleDataFromRegistry();
    let html = visualHtml !== null ? visualHtml : htmlContent;
    try {
      if (applicantContext) {
        Handlebars.registerHelper('uppercase', (str: string) => str?.toUpperCase?.() || str);
        Handlebars.registerHelper('lowercase', (str: string) => str?.toLowerCase?.() || str);
        Handlebars.registerHelper('formatDate', (date: string) => {
          if (!date) return '';
          try { return new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }); }
          catch { return date; }
        });
        const compiled = Handlebars.compile(html);
        html = compiled(data);
        if (data._rawApplication) {
          html = resolveMergeFields(html, data._rawApplication, '');
        }
      } else {
        html = html.replace(/\{\{(\w+(?:\.\w+)*)\}\}/g, (_match: string, key: string) => {
          const parts = key.split('.');
          let val: any = data;
          for (const p of parts) {
            if (val == null) return `{{${key}}}`;
            val = val[p];
          }
          return val != null ? String(val) : `{{${key}}}`;
        });
      }
    } catch (e) {
      console.warn('Preview rendering failed:', e);
    }
    const pageDimensions = paperSize === 'Letter'
      ? (orientation === 'landscape' ? { width: '11in', height: '8.5in' } : { width: '8.5in', height: '11in' })
      : paperSize === 'Legal'
      ? (orientation === 'landscape' ? { width: '14in', height: '8.5in' } : { width: '8.5in', height: '14in' })
      : (orientation === 'landscape' ? { width: '297mm', height: '210mm' } : { width: '210mm', height: '297mm' });

    return htmlContent
      ? `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <style>
    ${css}
    * { box-sizing: border-box; }
    html, body {
      margin: 0;
      padding: 0;
      background: #e2e8f0;
      color: #1e293b;
      font-family: sans-serif;
    }
    body {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 24px;
    }
    .page-canvas {
      width: ${pageDimensions.width};
      min-height: ${pageDimensions.height};
      padding: ${margins.top}mm ${margins.right}mm ${margins.bottom}mm ${margins.left}mm;
      background: #ffffff;
      box-shadow: 0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1);
      margin-bottom: 24px;
      position: relative;
    }
    .page-break {
      page-break-before: always;
      break-before: page;
      page-break-after: always;
      break-after: page;
      height: 24px;
      margin: 24px -${margins.right}mm;
      border-top: 2px dashed #cbd5e1;
      position: relative;
    }
    .page-break::after {
      content: 'PAGE BREAK';
      position: absolute;
      right: 12px;
      top: -10px;
      background: #f1f5f9;
      color: #64748b;
      font-size: 10px;
      font-weight: bold;
      padding: 0 6px;
      border-radius: 4px;
      border: 1px solid #cbd5e1;
    }
    @media print {
      body { background: #ffffff; padding: 0; }
      .page-canvas {
        width: auto;
        min-height: auto;
        box-shadow: none;
        margin: 0;
      }
      @page {
        size: ${paperSize} ${orientation};
        margin: ${margins.top}mm ${margins.right}mm ${margins.bottom}mm ${margins.left}mm;
      }
    }
  </style>
</head>
<body>
  <div class="page-canvas">
    ${html}
  </div>
</body>
</html>`
      : '<html><body style="font-family:sans-serif;color:#94a3b8;padding:40px;text-align:center;"><p>Start composing your document template content to render live page preview.</p></body></html>';
  }, [htmlContent, css, margins, paperSize, orientation, applicantContext, visualHtml, buildSampleDataFromRegistry]);

  /* ── Update preview iframe ── */
  useEffect(() => {
    const frame = previewFrameRef.current;
    if (!frame) return;
    const blob = new Blob([renderedHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    frame.src = url;
    return () => URL.revokeObjectURL(url);
  }, [renderedHtml, editorMode, editorTab]);

  /* ── Validate unknown and deprecated variables ── */
  const validate = useCallback((): string[] => {
    const errs: string[] = [];
    if (!name.trim()) errs.push('Template name is required');
    if (!htmlContent.trim()) errs.push('HTML document content is required');

    const unusedVars = htmlContent.match(/\{\{(\w+(?:\.\w+)*)\}\}/g);
    if (unusedVars) {
      const known = new Map<string, TemplateVariable>();
      registryVariables.forEach((v) => known.set(v.name, v));

      const unknownSet = new Set<string>();
      const deprecatedSet = new Set<string>();

      unusedVars.forEach((v) => {
        const varName = v.replace(/[{}]/g, '');
        if (varName.startsWith('#') || varName.startsWith('/')) return;
        const regVar = known.get(varName);
        if (!regVar) {
          unknownSet.add(varName);
        } else if (regVar.deprecated) {
          deprecatedSet.add(varName);
        }
      });

      if (unknownSet.size > 0) {
        errs.push(`Unknown variables: ${Array.from(unknownSet).join(', ')}`);
      }
      if (deprecatedSet.size > 0) {
        errs.push(`Deprecated variables referenced: ${Array.from(deprecatedSet).join(', ')}`);
      }
    }
    return errs;
  }, [name, htmlContent, registryVariables]);

  const handleValidate = useCallback(() => {
    const htmlErrs: string[] = [];
    if (htmlContent.trim()) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, 'text/html');
      const parseErrors = doc.querySelectorAll('parsererror');
      parseErrors.forEach((el) => {
        const txt = el.textContent || 'Unknown parse error';
        htmlErrs.push(`HTML: ${txt.substring(0, 180)}${txt.length > 180 ? '…' : ''}`);
      });
    }
    const openBlocks = (htmlContent.match(/\{\{#\w+/g) || []).length;
    const closeBlocks = (htmlContent.match(/\{\{\/\w+/g) || []).length;
    if (openBlocks !== closeBlocks) {
      htmlErrs.push(`Handlebars block tags mismatch: ${openBlocks} opened, ${closeBlocks} closed`);
    }

    const customErrs = validate();
    const finalErrs = [...htmlErrs, ...customErrs];
    setHtmlValidationErrors(finalErrs);

    if (finalErrs.length === 0) {
      setHtmlValidationErrors(['All checks passed — Document HTML looks valid ✓']);
      setTimeout(() => setHtmlValidationErrors([]), 2500);
    }
  }, [htmlContent, validate]);

  const handleFormat = useCallback(() => {
    try {
      const formatted = formatHtml(htmlContent);
      setHtmlContent(formatted);
    } catch (e) {
      console.warn('Format failed:', e);
    }
  }, [htmlContent]);

  const handleSave = () => {
    const customErrs = validate();
    setErrors(customErrs);
    if (customErrs.length) return;
    onSave({
      ...template,
      name,
      description,
      category,
      paperSize,
      orientation,
      margins,
      htmlContent,
      css,
    });
  };

  const handlePrint = () => {
    const iframe = previewFrameRef.current;
    if (iframe?.contentWindow) {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    }
  };

  const getAuthToken = async () => {
    const user = getAuth().currentUser;
    return user?.getIdToken() || '';
  };

  const handleGenerate = async () => {
    if (!template.id || !selectedApplicantId) return;
    setGenerating(true);
    try {
      const token = await getAuthToken();
      const res = await fetch('/api/generate-pdf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...workspaceHeader() },
        body: JSON.stringify({ templateId: template.id, applicantId: selectedApplicantId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Generation failed');
      setGenerateResult({ pdfUrl: data.pdfUrl, documentId: data.documentId });
    } catch (e: any) {
      notifyToast('error', 'PDF generation failed: ' + e.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async () => {
    if (!template.id || !selectedApplicantId) return;
    setGenerating(true);
    try {
      const token = await getAuthToken();
      const res = await fetch('/api/generate-pdf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...workspaceHeader() },
        body: JSON.stringify({ templateId: template.id, applicantId: selectedApplicantId, returnBuffer: true }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const selectedApplicant = applicantList.find(a => a.id === selectedApplicantId || a.appId === selectedApplicantId);
      a.download = buildApplicantPdfFileName(name, selectedApplicant?.appId || selectedApplicantId, selectedApplicant?.name);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e: any) {
      notifyToast('error', 'PDF download failed: ' + e.message);
    } finally {
      setGenerating(false);
    }
  };

  const filteredVariableGroups = useMemo(() => {
    const dedupeVars = (vars: TemplateVariable[]) => {
      const seen = new Set<string>();
      return vars.filter(v => {
        if (seen.has(v.name)) return false;
        seen.add(v.name);
        return true;
      });
    };

    const q = varSearch.trim().toLowerCase();
    return variableGroups.map(g => {
      const unique = dedupeVars(g.variables);
      const matched = q ? unique.filter(v => v.name.toLowerCase().includes(q) || v.label.toLowerCase().includes(q)) : unique;
      return { ...g, variables: matched };
    }).filter(g => g.variables.length > 0);
  }, [variableGroups, varSearch]);

  const combinedErrors = [...errors, ...htmlValidationErrors];
  const hasErrors = combinedErrors.length > 0 && !combinedErrors[0].includes('All checks passed');

  return (
    <Modal onClose={onClose} fullScreen>
      <div className="bg-slate-50 rounded-none shadow-2xl w-full h-full mx-auto flex flex-col overflow-hidden">
        {/* Document Studio Header */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-slate-200 shrink-0 bg-white">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-10 w-10 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center shrink-0">
              <FileText className="h-5 w-5 text-indigo-600" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-extrabold text-slate-900 truncate">
                  {template.id ? `Edit Document Template` : 'New Document Template'}
                </h2>
                {template.status && (
                  <span className={`px-2 py-0.5 text-[10px] font-extrabold rounded-md shrink-0 ${
                    template.status === 'published' ? 'bg-emerald-100 text-emerald-700' :
                    template.status === 'archived' ? 'bg-amber-100 text-amber-700' :
                    'bg-slate-100 text-slate-600'
                  }`}>
                    {template.status.toUpperCase()}
                  </span>
                )}
              </div>
              <p className="text-caption text-slate-400 font-medium truncate">
                {name || 'Untitled Template'} · v{template.version || 1} · {paperSize} ({orientation})
              </p>
            </div>
          </div>

          {/* Action Header Tools */}
          <div className="flex items-center gap-2.5">
            {/* Applicant Context Combobox */}
            <div ref={applicantDropdownRef} className="relative min-w-[16rem] max-w-xs">
              <div
                className="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl cursor-text focus-within:bg-white focus-within:border-indigo-500 transition-all"
                onClick={() => {
                  if (applicantSearch.trim().length > 0) {
                    setApplicantDropdownOpen(true);
                  }
                }}
              >
                <User className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                <input
                  type="text"
                  value={applicantSearch}
                  onChange={(e) => {
                    const val = e.target.value;
                    setApplicantSearch(val);
                    setApplicantDropdownOpen(val.trim().length > 0);
                  }}
                  onFocus={() => {
                    if (applicantSearch.trim().length > 0) {
                      setApplicantDropdownOpen(true);
                    }
                  }}
                  placeholder={selectedApplicantId ? 'Type to search candidates...' : 'Type name to select applicant...'}
                  className="flex-1 min-w-0 text-xs bg-transparent outline-none placeholder:text-slate-400 text-slate-800 font-medium"
                />
                {selectedApplicantId || applicantSearch ? (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedApplicantId('');
                      setApplicantSearch('');
                      setApplicantContext(null);
                      setApplicantDropdownOpen(false);
                    }}
                    className="text-slate-400 hover:text-slate-600 p-0.5 cursor-pointer"
                    title="Clear selection"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                ) : (
                  <Search className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                )}
              </div>
              {applicantDropdownOpen && applicantSearch.trim().length > 0 && (
                <div className="absolute top-full mt-1 left-0 right-0 z-[8500] bg-white border border-slate-200 rounded-xl shadow-xl max-h-72 overflow-y-auto py-1">
                  <div className="flex items-center justify-between px-3 py-1.5 border-b border-slate-100 bg-slate-50/50">
                    <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Matching Candidates</span>
                    <button
                      onClick={() => setApplicantDropdownOpen(false)}
                      className="p-0.5 text-slate-400 hover:text-slate-700 rounded transition-all cursor-pointer"
                      title="Close dropdown"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  {filteredApplicants.length === 0 ? (
                    <div className="px-4 py-3 text-xs text-slate-500">No matching applicants found</div>
                  ) : (
                    filteredApplicants.slice(0, 100).map((a) => (
                      <button
                        key={a.id}
                        onClick={() => {
                          setSelectedApplicantId(a.id);
                          setApplicantSearch(a.name);
                          setApplicantDropdownOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-xs rounded-lg mx-1 transition-all cursor-pointer ${
                          selectedApplicantId === a.id ? 'bg-indigo-50 text-indigo-900 font-bold' : 'text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-semibold truncate">{a.name}</span>
                          <span className="text-[11px] text-slate-500 shrink-0 ml-2 font-mono">{a.appId}</span>
                        </div>
                        {a.email && (
                          <div className="text-[11px] text-slate-500 truncate">{a.email}</div>
                        )}
                      </button>
                    ))
                  )}
                </div>
              )}
              {loadingApplicant && (
                <Loader className="absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-indigo-600 animate-spin" />
              )}
            </div>

            {/* Print & Generate Actions */}
            <button
              onClick={handlePrint}
              className="p-2 text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 border border-slate-200 rounded-xl transition-all cursor-pointer"
              title="Print document"
            >
              <Printer className="h-4 w-4" />
            </button>

            {selectedApplicantId && (
              <button
                onClick={handleDownload}
                disabled={generating}
                className="px-3 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 text-xs font-bold rounded-xl transition-all cursor-pointer flex items-center gap-1.5"
                title="Generate & Download PDF"
              >
                {generating ? <Loader className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                <span className="hidden sm:inline">Export PDF</span>
              </button>
            )}

            {/* Mode Toggle Switcher */}
            <div className="flex items-center bg-slate-100 p-0.5 rounded-xl border border-slate-200">
              <button
                onClick={() => { setEditorMode('visual'); setVisualHtml(null); }}
                className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer flex items-center gap-1 ${
                  editorMode === 'visual' ? 'bg-white text-slate-900 shadow-2xs' : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                <Eye className="h-3.5 w-3.5" /> Visual
              </button>
              <button
                onClick={() => { setEditorMode('html'); setVisualHtml(null); }}
                className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer flex items-center gap-1 ${
                  editorMode === 'html' ? 'bg-white text-slate-900 shadow-2xs' : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                <Code className="h-3.5 w-3.5" /> HTML
              </button>
              <button
                onClick={() => { setEditorMode('split'); setVisualHtml(null); }}
                className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer flex items-center gap-1 ${
                  editorMode === 'split' ? 'bg-white text-slate-900 shadow-2xs' : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                <Layout className="h-3.5 w-3.5" /> Split
              </button>
            </div>

            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 btn-primary text-white text-xs font-bold rounded-xl transition-all cursor-pointer disabled:opacity-50 flex items-center gap-1.5 shadow-2xs"
            >
              {saving ? <Loader className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              Save Template
            </button>

            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-xl transition-all cursor-pointer"
              title="Close editor"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Sub-toolbar Controls */}
        <div className="flex items-center justify-between px-6 py-2 border-b border-slate-200 bg-white shrink-0">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowSettings((s) => !s)}
              className={`px-3 py-1.5 text-xs font-bold rounded-xl transition-all cursor-pointer flex items-center gap-1.5 border ${
                showSettings ? 'bg-brand text-white border-brand' : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
              }`}
            >
              <Settings className="h-3.5 w-3.5" />
              <span>Page Setup & Settings</span>
            </button>

            <button
              onClick={() => setShowVars((s) => !s)}
              className={`px-3 py-1.5 text-xs font-bold rounded-xl transition-all cursor-pointer flex items-center gap-1.5 border ${
                showVars ? 'bg-brand-primary text-white border-brand-primary' : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
              }`}
            >
              <Hash className="h-3.5 w-3.5" />
              <span>Variable Inspector ({registryVariables.length})</span>
            </button>

            {editorMode !== 'visual' && (
              <>
                <div className="w-px h-4 bg-slate-200 mx-1" />
                <div className="flex items-center bg-slate-100 p-0.5 rounded-lg border border-slate-200">
                  <button
                    onClick={() => setEditorTab('html')}
                    className={`px-2.5 py-1 text-caption font-bold rounded-md transition-all cursor-pointer ${
                      editorTab === 'html' ? 'bg-white text-slate-900 shadow-2xs' : 'text-slate-500'
                    }`}
                  >
                    HTML
                  </button>
                  <button
                    onClick={() => setEditorTab('css')}
                    className={`px-2.5 py-1 text-caption font-bold rounded-md transition-all cursor-pointer ${
                      editorTab === 'css' ? 'bg-white text-slate-900 shadow-2xs' : 'text-slate-500'
                    }`}
                  >
                    CSS
                  </button>
                </div>

                <button
                  onClick={handleFormat}
                  className="px-2.5 py-1 text-caption font-bold text-slate-600 hover:bg-slate-100 rounded-lg transition-all cursor-pointer flex items-center gap-1"
                >
                  <AlignLeft className="h-3.5 w-3.5" /> Format
                </button>
              </>
            )}

            <button
              onClick={handleValidate}
              className="px-2.5 py-1 text-caption font-bold text-slate-600 hover:bg-slate-100 rounded-lg transition-all cursor-pointer flex items-center gap-1"
            >
              <CheckCircle className="h-3.5 w-3.5 text-emerald-600" /> Validate Syntax
            </button>
          </div>

          <div className="flex items-center gap-3 text-caption text-slate-400 font-medium">
            <span>Paper: <strong>{paperSize}</strong> ({orientation})</span>
            <span>·</span>
            <span>Margins: {margins.top}mm / {margins.right}mm</span>
          </div>
        </div>

        {/* Page Setup Drawer / Collapsible Settings */}
        {showSettings && (
          <div className="px-6 py-4 border-b border-slate-200 bg-white shrink-0 space-y-4 animate-fade-in shadow-inner">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-caption uppercase font-extrabold text-slate-500 mb-1">Document Name *</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 font-medium outline-none focus:bg-white focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-caption uppercase font-extrabold text-slate-500 mb-1">Category</label>
                <input
                  type="text"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  placeholder="e.g. DS-2019 / Sponsorship"
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 font-medium outline-none focus:bg-white focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-caption uppercase font-extrabold text-slate-500 mb-1">Paper Size</label>
                <select
                  value={paperSize}
                  onChange={(e) => setPaperSize(e.target.value as PaperSize)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 font-medium outline-none"
                >
                  <option value="A4">A4 (210 x 297 mm)</option>
                  <option value="Letter">US Letter (8.5 x 11 in)</option>
                  <option value="Legal">US Legal (8.5 x 14 in)</option>
                </select>
              </div>

              <div>
                <label className="block text-caption uppercase font-extrabold text-slate-500 mb-1">Orientation</label>
                <select
                  value={orientation}
                  onChange={(e) => setOrientation(e.target.value as Orientation)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 font-medium outline-none"
                >
                  <option value="portrait">Portrait</option>
                  <option value="landscape">Landscape</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-slate-100">
              <div>
                <label className="block text-caption uppercase font-extrabold text-slate-500 mb-1">Description</label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Brief description of when this document is issued..."
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 outline-none"
                />
              </div>

              <div>
                <label className="block text-caption uppercase font-extrabold text-slate-500 mb-1">Page Margins (mm) [Top / Right / Bottom / Left]</label>
                <div className="grid grid-cols-4 gap-2">
                  {(['top', 'right', 'bottom', 'left'] as const).map((side) => (
                    <input
                      key={side}
                      type="number"
                      value={margins[side]}
                      onChange={(e) => setMargins({ ...margins, [side]: parseInt(e.target.value) || 0 })}
                      className="px-2 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-mono text-center outline-none"
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Variable Inspector Drawer */}
        {showVars && (
          <div className="border-b border-slate-200 bg-indigo-50/50 p-4 shrink-0 max-h-64 overflow-y-auto space-y-3 animate-fade-in">
            <div className="flex items-center justify-between">
              <span className="text-xs font-extrabold text-indigo-900 uppercase tracking-wide">
                Available Document Merge Variables — Click to Copy or Insert
              </span>
              <input
                type="search"
                value={varSearch}
                onChange={e => setVarSearch(e.target.value)}
                placeholder="Filter variables..."
                className="px-3 py-1 bg-white border border-indigo-200 rounded-lg text-xs outline-none w-60"
              />
            </div>

            <div className="space-y-3">
              {filteredVariableGroups.map((group) => (
                <div key={group.label}>
                  <p className="text-[10px] font-extrabold text-indigo-400 mb-1 uppercase tracking-wider">{group.label}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {group.variables.map((v) => (
                      <button
                        key={v.id || v.name}
                        onClick={() => {
                          const tag = `{{${v.name}}}`;
                          if (editorTab === 'css') return;
                          htmlEditorRef.current?.insertText(tag);
                        }}
                        title={`${v.label} - Sample: "${v.sampleValue || 'N/A'}"`}
                        className="px-2.5 py-1 bg-white border border-indigo-200 hover:bg-indigo-100 text-indigo-800 text-xs font-mono font-bold rounded-lg transition-all cursor-pointer shadow-2xs"
                      >
                        {`{{${v.name}}}`}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Main Editor Canvas Body */}
        <div className="flex-1 min-h-0 flex overflow-hidden">
          {editorMode === 'visual' ? (
            <div className="flex-1 flex flex-col min-h-0">
              <TiptapEditor
                html={htmlContent}
                onChange={(newHtml) => {
                  setHtmlContent(newHtml);
                  setVisualHtml(newHtml);
                }}
                disabled={false}
                adminEmail={adminEmail}
                onVariableCreated={refreshVariables}
              />
            </div>
          ) : editorMode === 'html' ? (
            <div className="flex-1 flex flex-col min-h-0 bg-white">
              {editorTab === 'html' ? (
                <SmartHtmlEditor
                  ref={htmlEditorRef}
                  value={htmlContent}
                  onChange={setHtmlContent}
                  variables={allVariables}
                  className="flex-1"
                />
              ) : (
                <SmartHtmlEditor
                  value={css}
                  onChange={setCss}
                  variables={[]}
                  className="flex-1"
                />
              )}
            </div>
          ) : (
            <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 min-h-0 bg-slate-200 gap-px">
              {/* Code Editor Column */}
              <div className="min-w-0 flex flex-col bg-white">
                <div className="bg-slate-100 px-4 py-2 border-b border-slate-200 text-xs font-extrabold text-slate-700 uppercase tracking-wider flex items-center justify-between">
                  <span>{editorTab === 'html' ? 'Document HTML Code' : 'Global Document CSS'}</span>
                </div>
                {editorTab === 'html' ? (
                  <SmartHtmlEditor
                    ref={htmlEditorRef}
                    value={htmlContent}
                    onChange={setHtmlContent}
                    variables={allVariables}
                    className="flex-1"
                  />
                ) : (
                  <SmartHtmlEditor
                    value={css}
                    onChange={setCss}
                    variables={[]}
                    className="flex-1"
                  />
                )}
              </div>

              {/* Realistic Paper Page Live Preview Column */}
              <div className="min-w-0 flex flex-col bg-slate-200/80 overflow-auto p-6 items-center">
                <div className="bg-slate-100 px-4 py-1.5 rounded-xl border border-slate-300 text-xs font-bold text-slate-600 mb-4 shadow-2xs">
                  Live Rendered Page Preview
                </div>
                <div className="w-full max-w-[210mm] min-h-[297mm] bg-white shadow-2xl border border-slate-300 rounded-sm overflow-hidden transition-all">
                  <iframe
                    ref={previewFrameRef}
                    className="w-full h-[297mm] border-0 bg-white"
                    title="Live Document Preview"
                    sandbox="allow-same-origin"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Validation Error Banner */}
        {combinedErrors.length > 0 && (
          <div className="px-6 py-2.5 border-t border-slate-200 bg-white shrink-0 max-h-36 overflow-y-auto">
            {combinedErrors.map((err, i) => (
              <p key={i} className={`text-xs font-bold flex items-center gap-1.5 ${
                err.includes('All checks passed') ? 'text-emerald-700' : 'text-rose-700'
              }`}>
                {err.includes('All checks passed') ? <CheckCircle className="h-4 w-4 shrink-0" /> : <AlertCircle className="h-4 w-4 shrink-0" />}
                {err}
              </p>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}
